<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        />
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-dropdown
              v-if="getUserPermission('EXPORT_CHART_OF_ACCOUNT_REPORT')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :text="$t('Export')"
              variant="primary"
            >
              <b-dropdown-item
                v-for="item in exportTypes"
                :key="item.text"
                @click="item.handler"
              >
                {{ item.text }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-col>

      </b-row>

    </div>
    <div id="chat-of-account">
      <b-card id="prints">
        <h2 style="text-align: center;">
          {{ $t('CHART OF ACCOUNTS') }}
        </h2>
      </b-card>
      <b-table
        ref="glTableRef"
        :items="reportData"
        :busy="isBusy"
        :per-page="3000"
        :current-page="currentPage"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="position-relative"
      >

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>

      </b-table>
    </div>
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import XLSX from 'xlsx'
import { reportDataDetail } from '../../../../helpers/data'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    BDropdown,
    BDropdownItem,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isBusy: false,
      tableHeaders: [
        { key: 'description', label: 'Class of Account' },
        { key: 'level1Code', label: 'Level 1 Reporting' },
        { key: 'level2Code', label: 'Level 2 Reporting' },
        { key: 'level3Code', label: 'Level 3 Reporting' },
        { key: 'level4Code', label: 'Level 4 Reporting' },
      ],
      reportData: [],
      reportInfo: reportDataDetail,
      exportTypes: [
        {
          text: this.$t('CSV'),
          handler: () => this.csvExport(this.csvData),
        },
        {
          text: this.$t('Excel'),
          handler: () => this.excelExport(this.csvData),
        },
        {
          text: this.$t('PDF'),
          handler: () => this.pdfExport(),
        },
        {
          text: this.$t('Print'),
          handler: () => this.printData(),
        },
      ],
    }
  },
  computed: {
    csvData() {
      return this.reportData.map(item => ({
        'Class of Account': item.description,
        'Level 1 Reporting': item.level1Code,
        'Level 2 Reporting': item.level2Code,
        'Level 3 Reporting': item.level3Code,
        'Level 4 Reporting': item.level4Code,
      }))
    },
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },

  mounted() {
    this.fetchChartOfAccount()
  },
  methods: {
    ...mapActions({
      getChartOfAccount: 'reports/getChartOfAccount',
    }),
    async fetchChartOfAccount() {
      try {
        this.isBusy = true
        const response = await this.getChartOfAccount()
        const { data, status } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.isBusy = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    csvExport(arrData) {
      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += [
        ['CHART OF ACCOUNTS'],
        [],

        Object.keys(arrData[0]).join(','),
        ...arrData.map(item => Object.values(item).join(',')),
      ]
        .join('\n')
        .replace(/(^\[)|(\]$)/gm, '')

      const dataValue = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', dataValue)
      link.setAttribute('download', 'Chart of Account.csv')
      link.click()
    },

    excelExport(arrData) {
      const filename = 'Chart of Account.xlsx'
      const headerLists = [
        Object.keys(arrData[0]),
      ]

      const Heading = []
      const Data = arrData // All data lists
      const ws = XLSX.utils.aoa_to_sheet(Heading)
      XLSX.utils.sheet_add_aoa(ws, [
        ['CHART OF ACCOUNTS'],
      ], { origin: 'C1' })

      XLSX.utils.sheet_add_aoa(ws, headerLists, { origin: 'A4' })

      XLSX.utils.sheet_add_json(ws, Data, {
        skipHeader: true,
        origin: 'A5',
      })
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Chart of Account')
      XLSX.writeFile(wb, filename)
    },

    pdfExport() {
      const pdfData = this.reportData.map(item => ({
        ...item,
      }))

      const columns = [
        { title: 'Class of Account', key: 'description' },
        { title: 'Level 1 Reporting', key: 'level1Code' },
        { title: 'Level 2 Reporting', key: 'level2Code' },
        { title: 'Level 3 Reporting', key: 'level3Code' },
        { title: 'Level 4 Reporting', key: 'level4Code' },
      ]

      const doc = jsPDF('portrait', 'pt', 'a4')
      doc.setFontSize(20)
      doc.text(180, 40, 'CHART OF ACCOUNTS')
      doc.setFontSize(12)

      doc.autoTable({
        theme: 'striped', // grid, plain
        columns,
        body: pdfData,
        startY: 70,
        // split overflowing columns into pages
        horizontalPageBreak: true,
        // repeat this column in split pages
        horizontalPageBreakRepeat: 'id',
        tableWidth: 'auto', // 'auto', 'wrap' or a number,
        margin: {
          horizontal: 20,
          vertical: 20,
        },
        bodyStyles: {
          valign: 'top',
        },
        styles: {
          overflow: 'linebreak',
          cellWidth: 'wrap',
          maxCellHeight: 4,
          fontSize: 8,
          cellPadding: 5,
        },
        columnStyles: {
          text: {
            cellWidth: 'wrap',
          },
        },
      })
      // Footer page number
      const pages = doc.internal.getNumberOfPages()
      const pageWidth = doc.internal.pageSize.width
      const pageHeight = doc.internal.pageSize.height
      doc.setFontSize(5)

      for (let j = 1; j < pages + 1; j += 1) {
        const horizontalPos = pageWidth / 2 // Can be fixed number
        const verticalPos = pageHeight - 10 // Can be fixed number
        doc.setPage(j)
        doc.text(`page ${j} of ${pages}`, horizontalPos, verticalPos, { align: 'center' }) // Optional text styling});
      }
      doc.save('Chart of Account.pdf')
    },
    printData() {
      const divToPrint = document.getElementById('chat-of-account')
      const newWin = window.open('')
      newWin.document.write(divToPrint.outerHTML)
      newWin.print()
      newWin.close()
    },

    // printData() {
    //   const modal = document.getElementById('chat-of-account')
    //   const cloned = modal.cloneNode(true)
    //   let section = document.getElementById('print')
    //   if (!section) {
    //     section = document.createElement('div')
    //     section.id = 'print'
    //     document.body.appendChild(section)
    //   }
    //   section.innerHTML = ''
    //   section.appendChild(cloned)
    //   window.print()
    // },
  },
}
</script>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
    @media screen {
  #prints {
    display: none;
   }
}

@media print {
 body * {
  visibility:hidden;
  }
  #print, #print * {
    visibility:visible;
  }
  #print {
    position:absolute;
    left:0;
    top:0;
  }
}
</style>
